import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik } from 'formik'
import * as Yup from 'yup'
import {
  TabContent,
} from './styles'
import {
  FormSection,
  FormRow,
  FormActions,
  AuthSection,
  AuthTitle,
  ButtonGroup,
  UrlCopyWrapper,
  UrlText,
  CopyIcon,
} from './settings-styles'
import { Button, Input, Toggle, ClipBoardWrapper, MultiInput } from '../../../components'
import { ukrsibService } from '../../../api'
import { useUi } from '../../../context/ui.context'
import { icons } from '../../../icons'

type SettingsTabProps = {
  config: any
}

const SettingsTab: FC<SettingsTabProps> = ({ config }) => {
  const [t] = useTranslation()
  const { viewAlert } = useUi()
  const [loading, setLoading] = useState(false)

  const [authUrl, setAuthUrl] = useState<string>('')
  const [generatingUrl, setGeneratingUrl] = useState(false)
  const [authorizing, setAuthorizing] = useState(false)
  const [accounts, setAccounts] = useState<string[]>(config?.accounts || [])
  const [enabled, setEnabled] = useState<boolean>(config?.enabled || false)
  const [testMode, setTestMode] = useState<boolean>(config?.test_mode || false)
  const [toggleLoading, setToggleLoading] = useState<'enabled' | 'test_mode' | null>(null)

  // Update accounts when config changes
  React.useEffect(() => {
    setAccounts(config?.accounts || [])
    setEnabled(!!config?.enabled)
    setTestMode(!!config?.test_mode)
  }, [config])

  const initialValues = React.useMemo(() => ({
    state: config?.state || '',
    client_id: config?.client_id || '',
    client_secret: config?.client_secret || '',
    private_certificate: config?.private_certificate || '',
    private_passphrase: config?.private_passphrase || '',
    account: '', // field for MultiInput to work with
  }), [config])

  const initialValuesRef = React.useRef(initialValues)
  const initialAccountsRef = React.useRef<string[]>(config?.accounts || [])

  React.useEffect(() => {
    initialValuesRef.current = initialValues
    initialAccountsRef.current = config?.accounts || []
  }, [initialValues, config])

  const arraysEqual = (first: string[], second: string[]) => {
    if (first.length !== second.length) return false
    return first.every((value, index) => value === second[index])
  }

  const buildPayload = React.useCallback((values: any) => {
    const payload: any = {}
    const initial = initialValuesRef.current

    const fieldsToCompare: Array<keyof typeof initial> = [
      'state',
      'client_id',
      'client_secret',
      'private_certificate',
      'private_passphrase',
    ]

    fieldsToCompare.forEach((field) => {
      if (values[field] !== initial[field]) {
        payload[field] = values[field]
      }
    })

    const certificateChanged = Object.prototype.hasOwnProperty.call(payload, 'private_certificate')
    const certificateValue = certificateChanged ? values.private_certificate : initial.private_certificate
    const hasCertificate = Boolean(certificateValue)
    const passphraseChanged = values.private_passphrase !== initial.private_passphrase
    const hasOtherChanges = Object.keys(payload).length > 0

    if (certificateChanged) {
      payload.private_passphrase = values.private_passphrase ? values.private_passphrase : null
    } else if (hasCertificate && (passphraseChanged || hasOtherChanges)) {
      payload.private_passphrase = values.private_passphrase ? values.private_passphrase : null
    }

    if (!arraysEqual(accounts, initialAccountsRef.current)) {
      payload.accounts = accounts
    }

    delete payload.account
    return payload
  }, [accounts])

  const validationSchema = Yup.object({
    state: Yup.string().required(t('REQUIRED_FIELD')),
    client_id: Yup.string().required(t('REQUIRED_FIELD')),
    client_secret: Yup.string(),
    private_certificate: Yup.string(),
    private_passphrase: Yup.string(),
  })

  const changeAccounts = (account: string) => {
    const founded = accounts.find((item: any) => item === account)
    if (founded || account === '' || account === undefined) {
      setAccounts((prev: any) => {
        return prev.filter((item: any) => item !== account)
      })
    } else {
      setAccounts((prev: any) => [...prev, account])
    }
  }

  const handleToggleUpdate = async (field: 'enabled' | 'test_mode', value: boolean) => {
    if (toggleLoading) return

    const prevEnabled = enabled
    const prevTestMode = testMode

    if (field === 'enabled') {
      setEnabled(value)
    } else {
      setTestMode(value)
    }

    setToggleLoading(field)
    try {
      await ukrsibService.updateConfig({ [field]: value })
      const label = field === 'enabled' ? t('ENABLED') : t('TEST_MODE')
      viewAlert({
        severity: 'success',
        message: `${label}: ${value ? t('ENABLED') : t('DISABLED')}`,
      })
    } catch (error: any) {
      setEnabled(prevEnabled)
      setTestMode(prevTestMode)
      viewAlert({
        severity: 'error',
        message: error?.response?.data?.detail || t('ERROR_OCCURRED'),
      })
    } finally {
      setToggleLoading(null)
    }
  }

  const handleSubmit = async (values: any) => {
    setLoading(true)
    try {
      const payload = buildPayload(values)

      if (Object.keys(payload).length === 0) {
        viewAlert({
          severity: 'info',
          message: t('NO_CHANGES_TO_SAVE'),
        })
        return
      }

      await ukrsibService.updateConfig(payload)
      initialValuesRef.current = { ...initialValuesRef.current, ...values }
      initialAccountsRef.current = accounts
      viewAlert({
        severity: 'success',
        message: t('CONFIG_UPDATED_SUCCESSFULLY'),
      })
      // Note: Not calling updateConfig() to avoid page reload
      // The updated values are already in the form
    } catch (error: any) {
      viewAlert({
        severity: 'error',
        message: error?.response?.data?.detail || t('ERROR_OCCURRED'),
      })
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateAuthUrl = async () => {
    setGeneratingUrl(true)
    try {
      const response = await ukrsibService.generateAuthUrl()
      const url = response.data?.url

      if (url) {
        setAuthUrl(url)
        sessionStorage.setItem('ukrsib_auth_url', url)
        viewAlert({
          severity: 'success',
          message: t('AUTH_URL_GENERATED'),
        })
      } else {
        viewAlert({
          severity: 'error',
          message: 'Failed to get authorization URL',
        })
      }
    } catch (error: any) {
      viewAlert({
        severity: 'error',
        message: error?.response?.data?.detail || t('ERROR_OCCURRED'),
      })
    } finally {
      setGeneratingUrl(false)
    }
  }

  const handleAuthorize = async () => {
    setAuthorizing(true)
    try {
      await ukrsibService.authorize()
      // Clear URL after successful authorization
      setAuthUrl('')
      sessionStorage.removeItem('ukrsib_auth_url')
      viewAlert({
        severity: 'success',
        message: t('AUTHORIZATION_SUCCESSFUL'),
      })
    } catch (error: any) {
      viewAlert({
        severity: 'error',
        message: error?.response?.data?.detail || t('ERROR_OCCURRED'),
      })
    } finally {
      setAuthorizing(false)
    }
  }

  return (
    <TabContent>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={false}
      >
        {({ values, errors, touched, handleChange, handleBlur, submitForm }) => {
          const formikProps = {
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
          }
          const payloadPreview = buildPayload(values)
          const hasChanges = Object.keys(payloadPreview).length > 0

          return (
            <div>
              <FormSection>
                <FormRow>
                  <Toggle
                    value={enabled}
                    onChange={() => handleToggleUpdate('enabled', !enabled)}
                    title={t('ENABLED')}
                    customStyles={{ opacity: toggleLoading === 'enabled' ? 0.5 : 1, pointerEvents: toggleLoading ? 'none' : 'auto' }}
                  />
                </FormRow>

                <FormRow>
                  <Toggle
                    value={testMode}
                    onChange={() => handleToggleUpdate('test_mode', !testMode)}
                    title={t('TEST_MODE')}
                    customStyles={{ opacity: toggleLoading === 'test_mode' ? 0.5 : 1, pointerEvents: toggleLoading ? 'none' : 'auto' }}
                  />
                </FormRow>

                <FormRow>
                  <Input
                    required
                    fieldName="state"
                    title={t('STATE_COMPANY_NAME')}
                    placeholder={t('ENTER_COMPANY_NAME')}
                    {...formikProps}
                  />
                </FormRow>

                <FormRow>
                  <Input
                    required
                    fieldName="client_id"
                    title={t('CLIENT_ID')}
                    placeholder={t('ENTER_CLIENT_ID')}
                    {...formikProps}
                  />
                </FormRow>

                <FormRow>
                  <Input
                    fieldName="client_secret"
                    title={t('CLIENT_SECRET')}
                    placeholder={t('ENTER_CLIENT_SECRET')}
                    isPassword
                    toolTip={true}
                    toolTipText={t('OPTIONAL_FIELD')}
                    {...formikProps}
                  />
                </FormRow>

                <FormRow>
                  <Input
                    fieldName="private_certificate"
                    title={t('PRIVATE_CERTIFICATE')}
                    placeholder={t('ENTER_CERTIFICATE')}
                    isTextArea
                    customStyles={{ minHeight: '200px' }}
                    toolTip={true}
                    toolTipText={t('OPTIONAL_FIELD')}
                    {...formikProps}
                  />
                </FormRow>

                <FormRow>
                  <Input
                    fieldName="private_passphrase"
                    title={t('PRIVATE_PASSPHRASE')}
                    placeholder={t('ENTER_PASSPHRASE')}
                    isPassword
                    toolTip={true}
                    toolTipText={t('OPTIONAL_FIELD')}
                    {...formikProps}
                  />
                </FormRow>

                <FormRow>
                  <MultiInput
                    toolTip={true}
                    toolTipText={t('ACCOUNTS_TOOLTIP')}
                    placeholder={t('ACCOUNTS_PLACEHOLDER')}
                    fieldName='account'
                    tags={accounts}
                    title={t('ACCOUNTS')}
                    changeTags={changeAccounts}
                    isTextArea={true}
                    {...formikProps}
                  />
                </FormRow>

                <FormActions>
                  <Button
                    variant="primary"
                    size="medium"
                    title={t('SAVE_SETTINGS')}
                    disabled={loading || !hasChanges}
                    loading={loading}
                    onClick={submitForm}
                  />
                </FormActions>
              </FormSection>
            </div>
          )
        }}
      </Formik>

      <AuthSection>
        <AuthTitle>{t('AUTHORIZATION')}</AuthTitle>

        <ButtonGroup>
          <Button
            type="button"
            variant="secondary"
            size="medium"
            title={t('GENERATE_AUTH_URL')}
            onClick={handleGenerateAuthUrl}
            disabled={generatingUrl}
          />
          <Button
            type="button"
            variant="primary"
            size="medium"
            title={t('AUTHORIZE')}
            onClick={handleAuthorize}
            disabled={authorizing}
          />
        </ButtonGroup>

        {authUrl && (
          <ClipBoardWrapper toCopy={authUrl}>
            <UrlCopyWrapper>
              <UrlText>
                {authUrl}
                <CopyIcon src={icons.Copy} />
              </UrlText>
            </UrlCopyWrapper>
          </ClipBoardWrapper>
        )}
      </AuthSection>
    </TabContent>
  )
}

export default React.memo(SettingsTab)
